/*
 * The License Detail modal's Domain Binder tab (audit Phase 3.3). Replaces
 * wwwroot/js/License/Binder.js on the Phase 3.1 core. No jQuery.
 *
 * The binder is the recursive coverage tree:
 *
 *   DomainBinder (0..1 per license)
 *   |- ApplyToAllDomainMembers + AllApplications
 *   |- Domain chips (M:N)
 *   |- Properties
 *   `- ApplicationBinders (0..N)
 *      |- Application chips (M:N)
 *      |- Properties
 *      `- ResourceClaims
 *
 * Every verb here reloads the Domain Binder tab alone rather than the whole
 * modal, so an edit deep in an App Binder leaves the operator's Properties,
 * Assignments and Quotas tabs exactly where they were.
 *
 * An empty set means an empty set, at both levels: breadth over Applications
 * is the AllApplications flag and breadth over Resources is a claim on the
 * Application root with ClaimValue = ALL. That is why a new binder is created
 * with AllApplications false -- breadth is never the value you get by not
 * deciding.
 */
import { ajaxJson } from '../core/ajax';
import { one } from '../core/dom';
import { url as absoluteUrl } from '../core/http';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';
import { failToast, licenseId, readProperty, reloadTab } from './license';

const TAB = '#LicenseTabBinder';

/** The one Domain Binder card on the tab, and the id it carries. */
function binderCard(): Element | null {
    return one('.license-binder-tab .card[data-binder-id]');
}

function binderIdOf(): string {
    return binderCard()?.getAttribute('data-binder-id') ?? '';
}

function appBinderCard(appBinderId: number): Element | null {
    return one(`.lc-app-binder[data-app-binder-id="${appBinderId}"]`);
}

function checked(root: ParentNode, selector: string): boolean {
    return (root.querySelector(selector) as HTMLInputElement | null)?.checked === true;
}

function numberValue(root: ParentNode, selector: string): number {
    return Number((root.querySelector(selector) as HTMLInputElement | null)?.value ?? '');
}

/** Every write on this tab ends the same way. */
function afterWrite(message?: string): () => void {
    return () => {
        if (message) toast('success', message);
        void reloadTab(TAB);
    };
}

export const Binder = {
    // ----- Domain Binder header -----
    CreateDomainBinder(): void {
        const id = licenseId();
        if (!id) return;
        void ajaxJson({
            url: absoluteUrl(`License/${id}/DomainBinder`),
            method: 'POST',
            // AllApplications starts FALSE so a new binder grants nothing until
            // the operator says what it covers. The domain set starts empty for
            // the same reason.
            data: { LicenseId: id, ApplyToAllDomainMembers: false, AllApplications: false },
            errorTitle: 'Failed to create Domain Binder',
            onSuccess: afterWrite('Domain Binder created'),
            onFailure: failToast('Failed to create Domain Binder'),
        });
    },

    SaveDomainBinder(): void {
        const card = binderCard();
        if (!card) return;
        const binderId = card.getAttribute('data-binder-id') ?? '';
        void ajaxJson({
            url: absoluteUrl(`License/DomainBinder/${binderId}`),
            method: 'PUT',
            // AllApplications MUST be sent: the controller assigns it from the
            // model on every PUT, so omitting it would post the default false
            // and silently strip the license's breadth on an unrelated edit.
            data: {
                Id: binderId,
                ApplyToAllDomainMembers: checked(card, '.lc-binder-blanket'),
                AllApplications: checked(card, '.lc-binder-allapps'),
            },
            errorTitle: 'Failed to save Domain Binder',
            onSuccess: afterWrite('Domain Binder saved'),
            onFailure: failToast('Failed to save Domain Binder'),
        });
    },

    DeleteDomainBinder(): void {
        const binderId = binderIdOf();
        if (!binderId) return;
        void confirm({
            title: 'Delete Domain Binder',
            html: 'Delete the Domain Binder?<br/><br/>'
                + 'This cascades to every App Binder and Resource Claim under it. The license '
                + 'keeps its top-level Properties and Person / Group assignments, but '
                + '<span class="text-danger">loses all asset-coverage scope</span>.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`License/DomainBinder/${binderId}`),
                    method: 'DELETE',
                    errorTitle: 'Failed to delete Domain Binder',
                    onSuccess: afterWrite('Domain Binder deleted'),
                    onFailure: failToast('Failed to delete Domain Binder'),
                });
            },
        });
    },

    /**
     * Commit the Domain the picker just wrote into the hidden input.
     *
     * Fired by the picker's own change event (see enhanceBinderTab) rather
     * than by a separate Add button: without it the operator had to pick and
     * then press Add, which was easy to miss -- they would close the modal
     * believing the pick was committed and the selection was discarded.
     */
    AddBinderDomain(): void {
        const card = binderCard();
        if (!card) return;
        const binderId = card.getAttribute('data-binder-id') ?? '';
        const hidden = card.querySelector('.lc-add-binder-domain-id') as HTMLInputElement | null;
        const display = card.querySelector('.entity-picker-display') as HTMLInputElement | null;
        const domainId = Number(hidden?.value ?? '');
        if (!domainId || domainId <= 0) {
            toast('danger', 'Pick a domain first');
            return;
        }
        void ajaxJson({
            url: absoluteUrl(`License/DomainBinder/${binderId}/Domain/${domainId}`),
            method: 'POST',
            errorTitle: 'Failed to add domain',
            onSuccess: () => {
                // Clear the picker before the reload: a stale id left behind
                // would re-fire the auto-add on the next render.
                if (hidden) hidden.value = '';
                if (display) display.value = '';
                void reloadTab(TAB);
            },
            onFailure: failToast('Failed to add domain'),
        });
    },

    RemoveBinderDomain(domainId: number): void {
        const binderId = binderIdOf();
        if (!binderId) return;
        void ajaxJson({
            url: absoluteUrl(`License/DomainBinder/${binderId}/Domain/${domainId}`),
            method: 'DELETE',
            errorTitle: 'Failed to remove domain',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to remove domain'),
        });
    },

    AddDomainBinderProperty(): void {
        const card = binderCard();
        if (!card) return;
        const binderId = card.getAttribute('data-binder-id') ?? '';
        const property = readProperty(card, 'lc-add-dbprop');
        if (!property) return;
        void ajaxJson({
            url: absoluteUrl(`License/DomainBinder/${binderId}/Property`),
            method: 'POST',
            data: property,
            errorTitle: 'Failed to add property',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to add property'),
        });
    },

    DeleteDomainBinderProperty(propertyId: number): void {
        void ajaxJson({
            url: absoluteUrl(`License/DomainBinder/Property/${propertyId}`),
            method: 'DELETE',
            errorTitle: 'Failed to delete property',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to delete property'),
        });
    },

    // ----- Application Binders -----
    CreateAppBinder(): void {
        const binderId = binderIdOf();
        if (!binderId) return;
        void ajaxJson({
            url: absoluteUrl(`License/DomainBinder/${binderId}/AppBinder`),
            method: 'POST',
            data: { DomainBinderId: binderId },
            errorTitle: 'Failed to create App Binder',
            onSuccess: afterWrite('Application Binder created'),
            onFailure: failToast('Failed to create Application Binder'),
        });
    },

    DeleteAppBinder(appBinderId: number): void {
        void confirm({
            title: 'Delete Application Binder',
            html: 'Delete this Application Binder?<br/><br/>'
                + 'It cascades to the Resource Claims under it.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`License/AppBinder/${appBinderId}`),
                    method: 'DELETE',
                    errorTitle: 'Failed to delete App Binder',
                    onSuccess: afterWrite('App Binder deleted'),
                    onFailure: failToast('Failed to delete App Binder'),
                });
            },
        });
    },

    AddBinderApplication(appBinderId: number): void {
        const card = appBinderCard(appBinderId);
        if (!card) return;
        const applicationId = numberValue(card, '.lc-add-app-id');
        if (!applicationId || applicationId <= 0) {
            toast('danger', 'Pick an application first');
            return;
        }
        void ajaxJson({
            url: absoluteUrl(`License/AppBinder/${appBinderId}/Application/${applicationId}`),
            method: 'POST',
            errorTitle: 'Failed to add application',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to add application'),
        });
    },

    RemoveBinderApplication(appBinderId: number, applicationId: number): void {
        void ajaxJson({
            url: absoluteUrl(`License/AppBinder/${appBinderId}/Application/${applicationId}`),
            method: 'DELETE',
            errorTitle: 'Failed to remove application',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to remove application'),
        });
    },

    AddAppBinderProperty(appBinderId: number): void {
        const card = appBinderCard(appBinderId);
        if (!card) return;
        const property = readProperty(card, 'lc-add-abprop');
        if (!property) return;
        void ajaxJson({
            url: absoluteUrl(`License/AppBinder/${appBinderId}/Property`),
            method: 'POST',
            data: property,
            errorTitle: 'Failed to add property',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to add property'),
        });
    },

    DeleteAppBinderProperty(propertyId: number): void {
        void ajaxJson({
            url: absoluteUrl(`License/AppBinder/Property/${propertyId}`),
            method: 'DELETE',
            errorTitle: 'Failed to delete property',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to delete property'),
        });
    },

    // ----- Resource Claims -----
    AddResourceClaim(appBinderId: number): void {
        const card = appBinderCard(appBinderId);
        if (!card) return;
        const resourceId = numberValue(card, '.lc-add-rc-resource');
        if (!resourceId || resourceId <= 0) {
            toast('danger', 'Pick a target resource first');
            return;
        }
        const property = readProperty(card, 'lc-add-rc');
        if (!property) return;
        void ajaxJson({
            url: absoluteUrl(`License/AppBinder/${appBinderId}/ResourceClaim`),
            method: 'POST',
            data: { TargetResourceId: resourceId, ...property },
            errorTitle: 'Failed to add resource claim',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to add resource claim'),
        });
    },

    DeleteResourceClaim(claimId: number): void {
        void ajaxJson({
            url: absoluteUrl(`License/ResourceClaim/${claimId}`),
            method: 'DELETE',
            errorTitle: 'Failed to delete resource claim',
            onSuccess: afterWrite(),
            onFailure: failToast('Failed to delete resource claim'),
        });
    },
};

/**
 * Auto-commit a Domain as soon as the picker writes it back.
 *
 * An enhancer rather than a document-level listener: the binder tab is
 * re-rendered on every write, and one listener stamped onto each rendering is
 * both scoped to the tab and impossible to accumulate. The marker attribute is
 * what keeps a re-enhanced subtree from binding twice.
 */
export function enhanceBinderTab(root: ParentNode): void {
    const tabs: Element[] = [];
    if (root instanceof Element && root.matches('.license-binder-tab')) tabs.push(root);
    tabs.push(...Array.from(root.querySelectorAll('.license-binder-tab')));
    for (const tab of tabs) {
        if (tab.getAttribute('data-mcp-binder-bound') === 'true') continue;
        tab.setAttribute('data-mcp-binder-bound', 'true');
        tab.addEventListener('change', event => {
            const target = event.target;
            if (target instanceof HTMLInputElement && target.matches('.lc-add-binder-domain-id')) {
                const domainId = Number(target.value);
                if (domainId > 0) Binder.AddBinderDomain();
            }
        });
    }
}
